import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import paymentService from '../services/paymentService';
import { trackPurchase } from '../services/fbPixel';
import '../styles/PaymentConfirmationPage.css';

const PaymentConfirmationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const user = auth.currentUser;
  
  const { paymentId, status: initialStatus } = location.state || {};
  
  const [status, setStatus] = useState(initialStatus || 'pending');
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchaseTracked, setPurchaseTracked] = useState(false);

  // Fire Purchase pixel once when payment is approved
  useEffect(() => {
    if (status === 'approved' && !purchaseTracked) {
      trackPurchase({
        plan: paymentData?.subscriptionPlan || paymentData?.plan || 'subscription',
        amount: paymentData?.amount || 0,
        paymentId: paymentId || '',
      });
      setPurchaseTracked(true);
    }
  }, [status, purchaseTracked, paymentData, paymentId]);

  useEffect(() => {
    if (!paymentId) {
      navigate('/payment');
      return;
    }

    const fetchPaymentStatus = async () => {
      try {
        const data = await paymentService.getPaymentStatus(paymentId);
        setPaymentData(data);
        setStatus(data.status.toLowerCase());
      } catch (error) {
        console.error('Error fetching payment status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentStatus();
    
    // Poll for status updates every 10 seconds
    const interval = setInterval(fetchPaymentStatus, 10000);
    
    return () => clearInterval(interval);
  }, [paymentId, navigate]);

  const getStatusInfo = () => {
    switch (status) {
      case 'pending':
        return {
          icon: '⏳',
          title: 'Payment Under Review',
          message: 'Your payment is being verified. This usually takes a few minutes.',
          color: '#ffa502',
          actions: (
            <>
              <button className="primary-button" onClick={() => navigate('/sessions')}>
                Go to Sessions
              </button>
              <button className="secondary-button" onClick={() => window.location.reload()}>
                Check Status
              </button>
            </>
          )
        };
      case 'approved':
        return {
          icon: '✓',
          title: 'Payment Approved!',
          message: 'Your payment has been verified. Your subscription is now active!',
          color: '#26de81',
          actions: (
            <button className="primary-button" onClick={() => navigate('/sessions')}>
              Start Using Your Subscription
            </button>
          )
        };
      case 'rejected':
        return {
          icon: '✗',
          title: 'Payment Rejected',
          message: paymentData?.notes || 'Your payment could not be verified. Please try again or contact support.',
          color: '#ff4757',
          actions: (
            <>
              <button className="primary-button" onClick={() => navigate('/payment')}>
                Try Again
              </button>
              <button className="secondary-button" onClick={() => navigate('/contact')}>
                Contact Support
              </button>
            </>
          )
        };
      default:
        return {
          icon: '?',
          title: 'Unknown Status',
          message: 'Unable to determine payment status.',
          color: '#a0a0a0',
          actions: (
            <button className="primary-button" onClick={() => navigate('/payment')}>
              Back to Payment
            </button>
          )
        };
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  if (loading) {
    return (
      <div className="payment-confirmation-page">
        <div className="confirmation-container">
          <div className="loading-spinner"></div>
          <p>Loading payment status...</p>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo();

  return (
    <div className="payment-confirmation-page">
      <div className="confirmation-container">
        <div className="status-icon" style={{ color: statusInfo.color }}>
          {statusInfo.icon}
        </div>
        
        <h1>{statusInfo.title}</h1>
        <p className="status-message">{statusInfo.message}</p>

        {paymentData && (
          <div className="payment-details">
            <h3>Payment Details</h3>
            <div className="detail-row">
              <span className="label">Payment ID:</span>
              <span className="value">{paymentId}</span>
            </div>
            <div className="detail-row">
              <span className="label">Amount:</span>
              <span className="value">₱{paymentData.amount || 299}</span>
            </div>
            <div className="detail-row">
              <span className="label">Status:</span>
              <span className="value" style={{ color: statusInfo.color }}>
                {status.toUpperCase()}
              </span>
            </div>
            <div className="detail-row">
              <span className="label">Submitted:</span>
              <span className="value">
                {paymentData.submittedAt ? new Date(paymentData.submittedAt.seconds * 1000).toLocaleString() : 'N/A'}
              </span>
            </div>
          </div>
        )}

        <div className="action-buttons">
          {statusInfo.actions}
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmationPage;
